import { ImagePlus, LoaderCircle, RefreshCw, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export const PixQrUploader = ({ config, previewUrl, loadingPreview, onUpdated }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const selectFile = () => inputRef.current?.click();

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Envie uma imagem PNG, JPG ou WEBP.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5 MB.");
      return;
    }
    const formData = new FormData();
    formData.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/pix/qr-code", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await onUpdated(data);
      toast.success(config.has_qr_code ? "QR Code trocado." : "QR Code salvo.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/pix/qr-code");
      await onUpdated(data);
      toast.success("Imagem do QR Code excluída.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="pix-qr-section" className="rounded-3xl border-2 border-amber-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-bold uppercase tracking-wide text-amber-800">Pagamento manual</p>
      <h2 className="mt-1 font-heading text-2xl font-extrabold text-emerald-950">QR Code Pix</h2>
      <p className="mt-2 text-lg text-slate-600">Envie a imagem da sua conta para usar nas próximas fases.</p>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        onChange={upload}
        data-testid="pix-qr-upload-input"
        className="sr-only"
        aria-label="Enviar imagem do QR Code Pix"
      />
      <div data-testid="pix-qr-preview" className="pix-qr-well mt-6">
        {loadingPreview ? (
          <LoaderCircle className="h-10 w-10 animate-spin text-emerald-700" aria-label="Carregando imagem" />
        ) : previewUrl ? (
          <img src={previewUrl} alt="QR Code Pix cadastrado" className="h-full w-full object-contain p-4" />
        ) : (
          <div className="px-8 text-center">
            <ImagePlus className="mx-auto h-11 w-11 text-amber-700" />
            <p className="mt-3 text-lg font-bold text-emerald-950">Nenhuma imagem cadastrada</p>
            <p className="mt-1 text-base text-slate-600">PNG, JPG ou WEBP até 5 MB</p>
          </div>
        )}
      </div>
      <div className="mt-5 grid gap-3">
        <button
          type="button"
          onClick={selectFile}
          disabled={busy}
          data-testid="pix-qr-upload-button"
          className="inline-flex min-h-[56px] items-center justify-center gap-2 whitespace-nowrap rounded-xl bg-emerald-700 px-4 text-base font-bold text-white shadow-[0_5px_0_#064e3b] transition-[transform,box-shadow,background-color] hover:bg-emerald-800 active:translate-y-1 active:shadow-[0_1px_0_#064e3b] disabled:opacity-60"
        >
          {config.has_qr_code ? <RefreshCw className="h-5 w-5" /> : <ImagePlus className="h-5 w-5" />}
          {config.has_qr_code ? "Trocar imagem" : "Enviar imagem"}
        </button>
        {config.has_qr_code && (
          <button
            type="button"
            onClick={remove}
            disabled={busy}
            data-testid="pix-qr-delete-button"
            className="inline-flex min-h-[56px] items-center justify-center gap-2 whitespace-nowrap rounded-xl border-2 border-red-200 bg-white px-4 text-base font-bold text-red-800 transition-colors hover:bg-red-50 disabled:opacity-60"
          >
            <Trash2 className="h-5 w-5" /> Excluir imagem
          </button>
        )}
      </div>
      {config.qr_filename && (
        <p data-testid="pix-qr-filename" className="mt-4 text-base text-slate-600">
          Arquivo atual: {config.qr_filename}
        </p>
      )}
    </section>
  );
};